import React from "react";
import { Box } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import TranslateOutlined from "@mui/icons-material/TranslateOutlined";
import { dt } from "../../../shared/themes/redesignTokens";
import useTranslation, { SupportedLanguage } from "../../../shared/hooks/useTranslation";
import KioskBackButton from "./KioskBackButton";

const getLanguageDisplayName = (lng: SupportedLanguage): string => {
  const names: Record<SupportedLanguage, string> = {
    sk: "Sloven\u010Dina",
    en: "English",
    cs: "\u010Ce\u0161tina",
  };
  return names[lng];
};

const KioskSettingsPage: React.FC = () => {
  const { changeLanguage, getCurrentLanguage, getSupportedLanguages, translate } =
    useTranslation("public");

  const currentLanguage = getCurrentLanguage();
  const languages = getSupportedLanguages();

  const handleSelect = async (lng: SupportedLanguage) => {
    if (lng === currentLanguage) return;
    await changeLanguage(lng);
    window.location.reload();
  };

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        padding: "16px",
        paddingBottom: "100px",
      }}
    >
      <Box
        sx={{
          fontSize: "20px",
          fontWeight: 700,
          color: dt.textPrimary,
          mb: 3,
        }}
      >
        {translate("kioskSettings")}
      </Box>

      {/* Language Section */}
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          borderRadius: dt.radius,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            px: 2,
            pt: 2,
            pb: 1,
          }}
        >
          <TranslateOutlined sx={{ fontSize: "20px", color: dt.textSecondary }} />
          <Box
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: dt.textPrimary,
            }}
          >
            {translate("kioskLanguage")}
          </Box>
        </Box>

        <Box sx={{ px: 1, pb: 1 }}>
          {languages.map((lng) => (
            <Box
              key={lng}
              onClick={() => handleSelect(lng)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "52px",
                px: 2,
                borderRadius: dt.radiusSm,
                cursor: "pointer",
                userSelect: "none",
                WebkitTapHighlightColor: "transparent",
                transition: "background 150ms ease",
                backgroundColor:
                  lng === currentLanguage ? dt.blueSoft : "transparent",
                "&:active": {
                  backgroundColor: dt.blueSoft,
                },
              }}
            >
              <Box
                sx={{
                  fontSize: "15px",
                  fontWeight: lng === currentLanguage ? 600 : 400,
                  color:
                    lng === currentLanguage ? dt.blue : dt.textPrimary,
                }}
              >
                {getLanguageDisplayName(lng)}
              </Box>
              {lng === currentLanguage && (
                <CheckIcon sx={{ fontSize: "20px", color: dt.blue }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>

      <KioskBackButton />
    </Box>
  );
};

export default KioskSettingsPage;
